import { getDistance, setMeasurementAsNumber, tooltipTimeFormat } from 'src/utils';
import {
  TDistanceUnit,
  TVerticalDistanceUnit,
  meterToFoot,
  footToMeter,
  meterToKilometer,
  meterToMile,
  meterToNauticalMile,
} from '@ems/client-design-system';
import { GateType, OperationSummary } from 'src/app/gql/__generated__/hooks';
import { GateDeviationDataPoint, DeviationTooltip, TGateValues } from './types';
import { UNIT_FOOT, UNIT_KILOMETER, UNIT_METER, UNIT_MILE, UNIT_NAUTICAL_MILE } from 'src/constants';
import * as turf from '@turf/turf';

// Chart configuration constants
const CHART_CONFIG = {
  DECIMAL_UNITS: {
    STEP_SIZE: 0.25,
    PADDING: 0.5,
  },
  IMPERIAL_UNITS: {
    STEP_SIZE: 200,
    PADDING: 240,
  },
  METRIC_UNITS: {
    STEP_SIZE: 500,
    PADDING: 600,
  },
  ALTITUDE: {
    STEP_SIZE_FEET: 2000,
    STEP_SIZE_METERS: 500,
  },
  EXPORT: {
    BACKGROUND_COLOR: '#ffffff',
    SCALE: 2,
    FORMAT: 'image/jpeg',
    QUALITY: 1.0,
    FILE_PREFIX: 'gate-deviation-chart',
  },
  BUFFER_PERCENTAGE: 1.2,
  PRECISION: 100, // For rounding to 2 decimal places
} as const;

/**
 * Converts deviation and altitude values from ft to user's preferred units
 * Returns an array of data points, one for each gate intersection
 */
export const convertGateIntersectionData = (
  operation: OperationSummary,
  conversionSettings: {
    convertTo: TDistanceUnit;
    convertFrom: TDistanceUnit;
    returnValueType: 'string' | 'number';
  },
  conversionSettingsVertical: {
    convertTo: TVerticalDistanceUnit;
    convertFrom: TVerticalDistanceUnit;
    returnValueType: 'string' | 'number';
  }
): GateDeviationDataPoint[] => {
  const gateIntersections = operation.gateIntersections || [];

  if (gateIntersections.length === 0) {
    return [];
  }

  return gateIntersections.map(gateIntersection => {
    const deviationX = (() => {
      const rawDeviation = gateIntersection.deviationX || 0;
      const { convertFrom, convertTo } = conversionSettings;
      if (convertFrom === convertTo) {
        return rawDeviation;
      }
      if (
        convertFrom === UNIT_METER &&
        convertTo === UNIT_MILE
      ) {
        return meterToMile(rawDeviation);
      }

      if (
        convertFrom === UNIT_METER &&
        convertTo === UNIT_NAUTICAL_MILE
      ) {
        return meterToNauticalMile(rawDeviation);
      }

      if (
        convertFrom === UNIT_METER &&
        convertTo === UNIT_KILOMETER
      ) {
        return meterToKilometer(rawDeviation);
      }

      return getDistance(rawDeviation, conversionSettings) as number;
    })();

    const deviationY = convertGateVerticalDistance(
      gateIntersection.position.altitude || 0,
      conversionSettingsVertical
    );

    // Get altitude from position with safe fallback
    const altitude = gateIntersection.position?.altitude ?? 0;
    const convertedAltitude = convertGateVerticalDistance(altitude, conversionSettingsVertical);

    return {
      x: deviationX,
      y: deviationY,
      direction: (gateIntersection as any).direction ?? 'Unknown',
      time: (gateIntersection as any).time ?? new Date().toISOString(),
      altitude: convertedAltitude,
    };
  });
};

/**
 * Gets the color for an operation type from the theme color list
 */
export const getOperationColor = (
  operationType: string | undefined,
  colorList: Record<string, string>
): string => {
  const opType = operationType?.toLowerCase() || 'arrival';
  return colorList[opType] || colorList.arrival;
};

/**
 * Determines if the gate intersection direction is opposite
 */
export const isOppositeDirection = (operation: OperationSummary): boolean => {
  const gateIntersection = operation.gateIntersections?.[0];
  return (gateIntersection as any)?.direction === 'Opposite';
};

/**
 * Formats time for tooltip display (time only, no date)
 */
export const formatTooltipTime = (timeString: string): string =>
  tooltipTimeFormat(timeString) as string;


/**
 * Configuration for axis ranges and step sizes
 */
export interface AxisConfig {
  min: number;
  max: number;
  stepSize: number;
  ticks?: number[]; // Explicit tick values for precise control
}

/**
 * Generates nicely rounded tick values for chart axes
 * Uses powers of 10 to create clean, readable tick intervals
 *
 * @param max - Maximum value for the axis
 * @param min - Minimum value for the axis (used for filtering ticks)
 * @param isSymmetric - If true, generates ticks from -max to max (centered at 0). If false, generates from 0 to max
 * @returns Array of tick values
 */
const generateNiceTicks = (max: number, min: number, isSymmetric: boolean): number[] => {
  const ticks: number[] = [];

  if (max <= 0) {
    // If max is 0 or negative, just include 0
    ticks.push(0);
    return ticks;
  }

  // Calculate a nice step size that gives us up to 10 ticks per side
  const rawStepSize = max / 10;

  // Calculate a "nice" step size using powers of 10
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStepSize)));
  const normalized = rawStepSize / magnitude;

  // Round to 1, 2, or 5 times the magnitude
  let niceStepSize: number;
  if (normalized <= 1) {
    niceStepSize = 1 * magnitude;
  } else if (normalized <= 2) {
    niceStepSize = 2 * magnitude;
  } else if (normalized <= 5) {
    niceStepSize = 5 * magnitude;
  } else {
    niceStepSize = 10 * magnitude;
  }

  // Round max up to the next nice step size multiple to ensure clean boundaries
  const roundedMax = Math.ceil(max / niceStepSize) * niceStepSize;

  // Generate ticks based on symmetry requirement
  const startTick = isSymmetric ? -roundedMax : 0;
  const endTick = roundedMax;

  for (let tick = startTick; tick <= endTick; tick += niceStepSize) {
    // Round to avoid floating point precision issues
    const roundedTick = Math.round(tick * CHART_CONFIG.PRECISION) / CHART_CONFIG.PRECISION;
    // Only include ticks that are within the actual range
    const lowerBound = isSymmetric ? -max : min;
    if (roundedTick >= lowerBound && roundedTick <= max) {
      ticks.push(roundedTick);
    }
  }

  return ticks;
};

/**
 * Calculates the Y-axis configuration based on actual data points with a buffer
 *
 * @param gateValues - Gate values
 * @param isFeet - Whether the unit is feet (true) or meters (false)
 * @returns Y-axis configuration with min, max, step size, and ticks
 */
export const calculateYAxisConfig = (gateValues: TGateValues, isFeet: boolean): AxisConfig => {
  if (!gateValues) {
    return { min: 0, max: 0, stepSize: 0, ticks: [0] };
  }

  const stepSize = isFeet
    ? CHART_CONFIG.ALTITUDE.STEP_SIZE_FEET
    : CHART_CONFIG.ALTITUDE.STEP_SIZE_METERS;

  // Calculate min and max with buffer
  const minY = Math.min(gateValues?.floorAltitude || 0, gateValues?.ceilingAltitude || 0, 0);
  const maxY = Math.max(gateValues?.ceilingAltitude, gateValues?.floorAltitude, 0);

  // Apply buffer percentage to both min and max
  const minWithBuffer = minY * CHART_CONFIG.BUFFER_PERCENTAGE;
  const maxWithBuffer = maxY * CHART_CONFIG.BUFFER_PERCENTAGE;

  // Ensure min cannot go below 0
  const min = Math.max(0, Math.floor(minWithBuffer / stepSize) * stepSize);
  const max = Math.ceil(maxWithBuffer / stepSize) * stepSize;

  // Generate nicely rounded ticks (not symmetric, starts at 0)
  const ticks = generateNiceTicks(max, min, false);

  return { min, max, stepSize, ticks };
};

/**
 * Calculates the X-axis configuration based on actual data points with a buffer
 *
 * @param gateValues - Gate values
 * @param isFeet - Whether the unit is feet (true) or meters (false)
 * @param conversionSettings - Settings for unit conversion
 * @returns X-axis configuration with min, max, and step size (centered at 0)
 */
export const calculateXAxisConfig = (
  gateValues: TGateValues,
  isFeet: boolean,
  conversionSettings: {
    convertTo: TDistanceUnit;
    convertFrom: TDistanceUnit;
    returnValueType: 'string' | 'number';
  }
): AxisConfig => {
  if (!gateValues) {
    return { min: 0, max: 0, stepSize: 0, ticks: [0] };
  }

  // Determine step size and padding based on unit
  const isMiles = conversionSettings.convertTo === UNIT_MILE;
  const isNauticalMiles = conversionSettings.convertTo === UNIT_NAUTICAL_MILE;
  const isKilometers = conversionSettings.convertTo === UNIT_KILOMETER;
  const isDecimalUnit = isMiles || isNauticalMiles || isKilometers;

  const stepSize = isDecimalUnit
    ? CHART_CONFIG.DECIMAL_UNITS.STEP_SIZE
    : isFeet
      ? CHART_CONFIG.IMPERIAL_UNITS.STEP_SIZE
      : CHART_CONFIG.METRIC_UNITS.STEP_SIZE;

  const maxAbsX = gateValues?.gateRight;

  // For other units, use percentage buffer but ensure at least 2 ticks worth of padding
  const percentageBuffer = maxAbsX * CHART_CONFIG.BUFFER_PERCENTAGE;
  const maxWithBuffer = percentageBuffer;
  const max = maxWithBuffer;

  // Generate nicely rounded ticks (symmetric around 0)
  const ticks = generateNiceTicks(max, -max, true);

  return { min: -max, max, stepSize, ticks };
};

/**
 * Exports a chart container as a JPG image with white background
 *
 * @param containerElement - The HTML element containing the chart
 * @returns Promise that resolves when export is complete
 */
export const exportChartAsImage = async (containerElement: HTMLElement): Promise<void> => {
  const html2canvas = (await import('html2canvas')).default;

  const canvas = await html2canvas(containerElement, {
    backgroundColor: CHART_CONFIG.EXPORT.BACKGROUND_COLOR,
    scale: CHART_CONFIG.EXPORT.SCALE,
  });

  // Convert to JPG and trigger download
  const url = canvas.toDataURL(CHART_CONFIG.EXPORT.FORMAT, CHART_CONFIG.EXPORT.QUALITY);
  const link = document.createElement('a');
  const dateString = new Date().toISOString().split('T')[0];
  link.download = `${CHART_CONFIG.EXPORT.FILE_PREFIX}-${dateString}.jpg`;
  link.href = url;
  link.click();
};

/**
 * Extracts tooltip rendering data from a tooltip reference and chart data
 *
 * Retrieves the dataset, data point, and label information needed to render
 * a tooltip component. This helper function centralizes the logic for extracting
 * tooltip data, reducing code duplication between hover and pinned tooltip rendering.
 *
 * @param tooltip - The tooltip object containing datasetIndex, dataIndex, and position coordinates
 * @param chartData - The Chart.js data object containing all datasets
 * @returns An object containing the dataset, dataPoint, label, intersectionIndex, and totalIntersections, or null if the data is invalid
 */
export const getTooltipRenderData = (
  tooltip: DeviationTooltip,
  chartData: any
): {
  dataset: any;
  dataPoint: GateDeviationDataPoint;
  label: string;
  intersectionIndex: number;
  totalIntersections: number;
} | null => {
  const dataset = chartData.datasets[tooltip.datasetIndex];
  const dataPoint = dataset?.data[tooltip.dataIndex];

  if (!dataset || !dataPoint) {
    return null;
  }

  const totalIntersections = dataset.data.length;
  const intersectionIndex = Number(tooltip.dataIndex) + 1; // 1-based index for display

  return {
    dataset,
    dataPoint,
    label: dataset.label,
    intersectionIndex,
    totalIntersections,
  };
};

/**
 * Calculates the width of a gate based on its left and right position geometry.
 *
 * The function calculates the distance between the gate's left and right positions
 * and returns the total width in the specified units.
 *
 * @param gate - The gate object containing geometry with leftPosition and rightPosition
 * @param units - The units to use for distance calculation (e.g., 'feet', 'meters', 'kilometers', 'miles', 'nauticalmiles')
 * @returns The total width of the gate in the specified units
 *
 * @example
 * const gateWidth = getGateXCoordinates(gate, 'feet');
 * // Returns: 1000
 * // This means the gate has a total width of 1000 feet
 */
export const getGateXCoordinates = (gate: GateType, units: turf.Units): number => {
  const leftPoint = [gate.geometry?.leftPosition?.longitude, gate.geometry?.leftPosition?.latitude];
  const rightPoint = [
    gate.geometry?.rightPosition?.longitude,
    gate.geometry?.rightPosition?.latitude,
  ];

  return turf.distance(turf.point(leftPoint), turf.point(rightPoint), { units });
};

/**
 * Converts vertical distance values for gate calculations
 * Only switches between feet (ft) and meters (m) for gate vertical measurements
 *
 * @param value - The distance value to convert (can be string or number)
 * @param params - Conversion parameters
 * @param params.convertTo - Units that the value will be returned in (TVerticalDistanceUnit | 'metricBaseUnit' | 'imperialBaseUnit')
 * @param params.convertFrom - Units that the value is provided in (TVerticalDistanceUnit)
 * @param params.returnValueType - Type of value to return ('number' | 'string') - Note: currently always returns number
 * @returns The converted distance value as a number
 *
 * @example
 * // Convert 1000 meters to feet
 * const feet = convertGateVerticalDistance(1000, {
 *   convertFrom: 'm',
 *   convertTo: 'ft',
 *   returnValueType: 'number'
 * });
 * // Returns: ~3280.84
 */
export const convertGateVerticalDistance = (
  value: string | number,
  params: {
    // Units that the value will be returned in
    convertTo: TVerticalDistanceUnit | 'metricBaseUnit' | 'imperialBaseUnit';
    // Units that the value is provided in
    convertFrom: TVerticalDistanceUnit;
    returnValueType: 'number' | 'string';
  }
): number => {
  const valueNumber = setMeasurementAsNumber(String(value))

  const { convertTo, convertFrom } = params;
  if (convertTo === convertFrom) {
    return valueNumber;
  } else if (convertFrom === UNIT_METER) {
    if (convertTo === UNIT_FOOT) {
      return Number(meterToFoot(valueNumber).toFixed(2));
    }
  } else if (convertFrom === UNIT_FOOT) {
    if (convertTo === UNIT_METER) {
      return Math.round(footToMeter(valueNumber));
    }
  }
  return valueNumber;
};